import type { WorldRoomDirectory, WorldSessionProjection } from "@cleo/contracts";
import type { RootSessionStatus } from "~/rootSessionStrip";
import type { RoomRailGroups, RoomRailRoom, RoomRailSessionNode } from "./projectRoomRailGroups";

export const ROOM_RAIL_SORTS = {
  manual: "Manual order",
  recent: "Recent activity",
  created: "Newest created",
  name: "Name A–Z",
} as const;
export type RoomRailSort = keyof typeof ROOM_RAIL_SORTS;
export type RoomRailFilter = "all" | "needs" | "working";
/**
 * Can 80 (the principal, 17 Oct 2026: "the quantity of sessions is
 * overwhelming"): sessions untouched this many days fold under Earlier at the
 * foot of each room; 0 keeps every session in view.
 */
export const ROOM_RAIL_QUIET_DAYS = [0, 1, 3, 7, 14] as const;
export type RoomRailQuietDays = (typeof ROOM_RAIL_QUIET_DAYS)[number];
export interface RoomRailPreferences {
  roomSort: RoomRailSort;
  sessionSort: RoomRailSort;
  flatSort: Exclude<RoomRailSort, "manual">;
  view: "rooms" | "sessions";
  filter: RoomRailFilter;
  quietDays: RoomRailQuietDays;
  roomOrder: string[];
  /** The room cards' personal order and pins (the card rail, 16 Oct 2026). */
  cardOrder: string[];
  pinnedCardIds: string[];
  launcherOrder: string[];
  contextViewOrder: string[];
  pinnedSessionIds: string[];
  sessionOrders: Record<string, string[]>;
  sortOverrides: Record<string, RoomRailSort>;
  roomFolds: Record<string, boolean>;
  sessionFolds: Record<string, boolean>;
  /**
   * The principal's decision of 6 Oct 2026: archived rooms and retired
   * sessions stay out of the rail unless the person turns these on. One
   * choice per person for the whole World; nothing is deleted either way.
   */
  showArchivedRooms: boolean;
  showRetiredSessions: boolean;
}
export const DEFAULT_ROOM_RAIL_PREFERENCES: RoomRailPreferences = {
  roomSort: "manual",
  sessionSort: "manual",
  flatSort: "recent",
  view: "rooms",
  filter: "all",
  quietDays: 3,
  roomOrder: [],
  cardOrder: [],
  pinnedCardIds: [],
  launcherOrder: [],
  contextViewOrder: [],
  pinnedSessionIds: [],
  sessionOrders: {},
  sortOverrides: {},
  roomFolds: {},
  sessionFolds: {},
  showArchivedRooms: false,
  showRetiredSessions: false,
};
export function roomRailScope(
  identity: string | null | undefined,
  actorId: string | undefined,
  worldKey: string,
): string | null {
  return identity ? JSON.stringify([identity, actorId ?? null, worldKey]) : null;
}
export function parseRoomRailPreferences(value: unknown): RoomRailPreferences {
  const source = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const sort = (v: unknown, fallback: RoomRailSort): RoomRailSort =>
    typeof v === "string" && Object.hasOwn(ROOM_RAIL_SORTS, v) ? (v as RoomRailSort) : fallback;
  const ids = (v: unknown): string[] =>
    Array.isArray(v)
      ? [
          ...new Set(
            v.filter(
              (id): id is string => typeof id === "string" && id.length > 0 && id.length < 512,
            ),
          ),
        ]
      : [];
  const record = (v: unknown) =>
    v && typeof v === "object" && !Array.isArray(v) ? Object.entries(v) : [];
  return {
    roomSort: sort(source.roomSort, "manual"),
    sessionSort: sort(source.sessionSort, "manual"),
    flatSort:
      source.flatSort === "manual"
        ? "recent"
        : (sort(source.flatSort, "recent") as RoomRailPreferences["flatSort"]),
    view: source.view === "sessions" ? "sessions" : "rooms",
    filter: source.filter === "needs" || source.filter === "working" ? source.filter : "all",
    quietDays: (ROOM_RAIL_QUIET_DAYS as readonly unknown[]).includes(source.quietDays)
      ? (source.quietDays as RoomRailQuietDays)
      : 3,
    roomOrder: ids(source.roomOrder),
    cardOrder: ids(source.cardOrder),
    pinnedCardIds: ids(source.pinnedCardIds),
    launcherOrder: ids(source.launcherOrder),
    contextViewOrder: ids(source.contextViewOrder),
    pinnedSessionIds: ids(source.pinnedSessionIds),
    sessionOrders: Object.fromEntries(
      record(source.sessionOrders).map(([key, v]) => [key, ids(v)]),
    ),
    sortOverrides: Object.fromEntries(
      record(source.sortOverrides).filter(
        ([, v]) => typeof v === "string" && Object.hasOwn(ROOM_RAIL_SORTS, v),
      ),
    ),
    roomFolds: Object.fromEntries(
      record(source.roomFolds).filter(([, v]) => typeof v === "boolean"),
    ),
    sessionFolds: Object.fromEntries(
      record(source.sessionFolds).filter(([, v]) => typeof v === "boolean"),
    ),
    showArchivedRooms: source.showArchivedRooms === true,
    showRetiredSessions: source.showRetiredSessions === true,
  };
}
export function sessionOrderFor(
  prefs: RoomRailPreferences,
  parentId: string,
  roomId: string,
): RoomRailSort {
  return prefs.sortOverrides[parentId] ?? prefs.sortOverrides[roomId] ?? prefs.sessionSort;
}
function sortEntries<T>(
  entries: readonly T[],
  sort: RoomRailSort,
  manual: readonly string[],
  read: (entry: T) => { id: string; label: string; born: number; activity: number },
): T[] {
  const rank = new Map(manual.map((id, i) => [id, i]));
  return [...entries].sort((a, b) => {
    const left = read(a),
      right = read(b);
    const tie = left.born - right.born || left.id.localeCompare(right.id);
    if (sort === "manual")
      return (rank.get(left.id) ?? manual.length) - (rank.get(right.id) ?? manual.length) || tie;
    if (sort === "name")
      return (
        left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: "base" }) ||
        tie
      );
    if (sort === "created") return right.born - left.born || tie;
    return right.activity - left.activity || tie;
  });
}
/** Display-only projection over the existing membership fold; never rewrite parentage or birth order. */
export function arrangeRoomRailGroups(
  groups: RoomRailGroups,
  prefs: RoomRailPreferences,
  activity: ReadonlyMap<string, number>,
): RoomRailGroups {
  const activityByNode = new Map<string, number>();
  const measure = (node: RoomRailSessionNode): number => {
    const own = activity.get(node.session.sessionId) ?? 0;
    const value = Math.max(Number.isFinite(own) ? own : 0, ...node.children.map(measure));
    activityByNode.set(node.session.sessionId, value);
    return value;
  };
  const roomActivity = new Map(
    groups.rooms.map((entry) => [entry.room.roomId, Math.max(0, ...entry.sessions.map(measure))]),
  );
  const nodes = (
    entries: readonly RoomRailSessionNode[],
    parentId: string,
    roomId: string,
  ): RoomRailSessionNode[] =>
    sortEntries(
      entries,
      sessionOrderFor(prefs, parentId, roomId),
      prefs.sessionOrders[parentId] ?? [],
      ({ session }) => ({
        id: session.sessionId,
        label: session.label,
        born: session.bornSeq,
        activity: activityByNode.get(session.sessionId) ?? 0,
      }),
    ).map((node) => ({ ...node, children: nodes(node.children, node.session.sessionId, roomId) }));
  return {
    ...groups,
    rooms: sortEntries(groups.rooms, prefs.roomSort, prefs.roomOrder, ({ room }) => ({
      id: room.roomId,
      label: room.label,
      born: room.bornSeq,
      activity: roomActivity.get(room.roomId) ?? 0,
    })).map((entry) => ({
      ...entry,
      sessions: nodes(entry.sessions, entry.room.roomId, entry.room.roomId),
    })),
  };
}
export function matchesRoomRailFilter(
  status: RootSessionStatus | null | undefined,
  filter: RoomRailFilter,
): boolean {
  return (
    filter === "all" ||
    (filter === "needs" && (status === "input" || status === "approval" || status === "failed")) ||
    (filter === "working" && status === "working")
  );
}
export function filterRoomRailGroups(
  groups: RoomRailGroups,
  filter: RoomRailFilter,
  statusOf: (id: string) => RootSessionStatus | null,
): RoomRailGroups {
  if (filter === "all") return groups;
  const nodes = (entries: readonly RoomRailSessionNode[]): RoomRailSessionNode[] =>
    entries.flatMap((node) => {
      const children = nodes(node.children);
      return matchesRoomRailFilter(statusOf(node.session.sessionId), filter) || children.length
        ? [{ ...node, children }]
        : [];
    });
  return {
    ...groups,
    rooms: groups.rooms
      .map((entry) => ({ ...entry, sessions: nodes(entry.sessions) }))
      .filter((entry) => entry.sessions.length > 0),
  };
}
/** Every session in the arranged tree, preserving sibling order and room location. */
export function roomRailSessionEntries(groups: RoomRailGroups) {
  const entries: { node: RoomRailSessionNode; room: RoomRailRoom; breadcrumb: string }[] = [];
  const walk = (nodes: readonly RoomRailSessionNode[], room: RoomRailRoom, path: string[]) => {
    for (const node of nodes) {
      entries.push({ node: { ...node, children: [] }, room, breadcrumb: path.join(" / ") });
      walk(node.children, room, [...path, node.session.label]);
    }
  };
  groups.rooms.forEach((room) => walk(room.sessions, room, [room.room.label]));
  return entries;
}
export function flatRoomRailSessions(
  groups: RoomRailGroups,
  prefs: RoomRailPreferences,
  activity: ReadonlyMap<string, number>,
  statusOf: (id: string) => RootSessionStatus | null,
) {
  const entries = roomRailSessionEntries(groups).filter(({ node }) =>
    matchesRoomRailFilter(statusOf(node.session.sessionId), prefs.filter),
  );
  return sortEntries(entries, prefs.flatSort, [], ({ node: { session } }) => ({
    id: session.sessionId,
    label: session.label,
    born: session.bornSeq,
    activity: activity.get(session.sessionId) ?? 0,
  }));
}

/** Pins are exact-session shortcuts across both room kinds; room membership is unchanged. */
export function pinnedRoomRailSessions(
  index: WorldRoomDirectory,
  pinnedSessionIds: readonly string[],
) {
  const rooms = new Map(
    index.rooms.filter((room) => !room.archived).map((room) => [room.roomId, room]),
  );
  const sessions = new Map(index.sessions.map((session) => [session.sessionId, session]));
  return pinnedSessionIds.flatMap((id) => {
    const session = sessions.get(id);
    const room = session && rooms.get(session.roomId);
    if (!session || !room || session.lifecycle === "retired") return [];
    const ancestors: string[] = [];
    const visited = new Set([id]);
    let parent = sessions.get(session.parentId);
    while (parent && parent.roomId === room.roomId && !visited.has(parent.sessionId)) {
      visited.add(parent.sessionId);
      ancestors.unshift(parent.label);
      parent = sessions.get(parent.parentId);
    }
    return [
      {
        node: { session, children: [], addressed: false } satisfies RoomRailSessionNode,
        breadcrumb: [room.label, ...ancestors].join(" / "),
      },
    ];
  });
}
/** Keep non-visible siblings in their places; only the explicitly moved item changes position. */
export function moveRoomRailItem(
  prefs: RoomRailPreferences,
  groups: RoomRailGroups,
  kind: "room" | "session" | "pin",
  id: string,
  overId: string,
  after: boolean,
): RoomRailPreferences | null {
  if (id === overId) return null;
  if (kind === "pin") {
    if (!prefs.pinnedSessionIds.includes(id) || !prefs.pinnedSessionIds.includes(overId))
      return null;
    const pins = prefs.pinnedSessionIds.filter((key) => key !== id);
    pins.splice(pins.indexOf(overId) + (after ? 1 : 0), 0, id);
    return { ...prefs, pinnedSessionIds: pins };
  }
  let parent: string | null = null;
  let siblings: readonly string[] = [];
  if (kind === "room") siblings = groups.rooms.map((entry) => entry.room.roomId);
  else {
    const find = (nodes: readonly RoomRailSessionNode[]) => {
      if (nodes.some((node) => node.session.sessionId === id)) {
        parent = nodes.find((node) => node.session.sessionId === id)!.session.parentId;
        siblings = nodes.map((node) => node.session.sessionId);
      } else nodes.forEach((node) => find(node.children));
    };
    groups.rooms.forEach((room) => find(room.sessions));
  }
  if (!siblings.includes(id) || !siblings.includes(overId)) return null;
  const order = siblings.filter((key) => key !== id);
  order.splice(order.indexOf(overId) + (after ? 1 : 0), 0, id);
  return kind === "room"
    ? { ...prefs, roomSort: "manual", roomOrder: order }
    : parent === null
      ? null
      : {
          ...prefs,
          sessionOrders: { ...prefs.sessionOrders, [parent]: order },
          sortOverrides: { ...prefs.sortOverrides, [parent]: "manual" },
        };
}

export function roomRailSessionSiblings(
  groups: RoomRailGroups,
  session: Pick<WorldSessionProjection, "parentId">,
): readonly string[] {
  let result: readonly string[] = [];
  const walk = (nodes: readonly RoomRailSessionNode[]) => {
    if (nodes[0]?.session.parentId === session.parentId)
      result = nodes.map((node) => node.session.sessionId);
    else nodes.forEach((node) => walk(node.children));
  };
  groups.rooms.forEach((room) => walk(room.sessions));
  return result;
}
